import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  TabMenu,
  TabMenuContent,
  TabMenuList,
  TabMenuTrigger,
  Tag,
  useAlert,
} from '@ads/components-react';
import {
  ArrowPathRoundedSquareIcon,
  EllipsisHorizontalIcon,
  KeyIcon,
  LockClosedIcon,
  MapPinIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/shared/ui/atoms';
import { useCancelEnrollment } from '../../../hooks/useCancelEnrollment';
import { useChangePolo } from '../../../hooks/useChangePolo';
import { useEnrollmentCourseAccess } from '../../../hooks/useEnrollmentCourseAccess';
import { useEnrollmentManagement } from '../../../hooks/useEnrollmentDetail';
import { useEnrollmentDetails } from '../../../hooks/useEnrollmentDetails';
import { useEnrollmentDocuments } from '../../../hooks/useEnrollmentDocuments';
import { useEnrollmentFinancial } from '../../../hooks/useEnrollmentFinancial';
import { useEnrollmentHistory } from '../../../hooks/useEnrollmentHistory';
import { useEnrollmentManagementTabs } from '../../../hooks/useEnrollmentManagementTabs';
import { useEnrollmentScholarship } from '../../../hooks/useEnrollmentScholarship';
import { useResetPassword } from '../../../hooks/useResetPassword';
import { useTemporaryPassword } from '../../../hooks/useTemporaryPassword';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';
import { CancelEnrollmentModal } from '../CancelEnrollmentModal';
import { ChangePoloModal } from '../ChangePoloModal';
import { EnrollmentAcademicTab } from '../EnrollmentAcademicTab';
import { EnrollmentCourseAccessDrawer } from '../EnrollmentCourseAccessDrawer';
import { EnrollmentDetailsTab } from '../EnrollmentDetailsTab';
import { EnrollmentDocumentationTab } from '../EnrollmentDocumentationTab';
import { EnrollmentFinancialTab } from '../EnrollmentFinancialTab';
import { EnrollmentHistoryTab } from '../EnrollmentHistoryTab';
import { EnrollmentScholarshipTab } from '../EnrollmentScholarshipTab';
import { EnrollmentSelectiveProcessDrawer } from '../EnrollmentSelectiveProcessDrawer';
import { EnrollmentStatusDrawer } from '../EnrollmentStatusDrawer';
import { ResetPasswordModal } from '../ResetPasswordModal';
import { TemporaryPasswordModal } from '../TemporaryPasswordModal';

export interface EnrollmentManagementFormProps {
  readonly enrollmentId: number;
  readonly onBack: () => void;
  readonly onOpenPayments?: () => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  return (a + (b || a)).toUpperCase();
}

function relativeMonths(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const months = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
  if (months <= 0) return 'há menos de 1 mês';
  return `há ${months} ${months === 1 ? 'mês' : 'meses'}`;
}

/**
 * Formulário principal de gestão de matrícula.
 * Exibe dados do aluno, vínculo com o curso e ações disponíveis.
 */
export function EnrollmentManagementForm({
  enrollmentId,
  onOpenPayments,
}: EnrollmentManagementFormProps) {
  const { t } = useTranslation('personManagement');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const [isStatusDrawerOpen, setIsStatusDrawerOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { cancel } = useCancelEnrollment(() => setIsCancelModalOpen(false));
  const [isChangePoloModalOpen, setIsChangePoloModalOpen] = useState(false);
  const { changePolo, isChanging } = useChangePolo(() => setIsChangePoloModalOpen(false));
  const [isCourseAccessDrawerOpen, setIsCourseAccessDrawerOpen] = useState(false);
  const [isSelectiveProcessDrawerOpen, setIsSelectiveProcessDrawerOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isTempPasswordModalOpen, setIsTempPasswordModalOpen] = useState(false);
  const [isTempPasswordSuccess, setIsTempPasswordSuccess] = useState(false);

  const { resetPassword, isResetting } = useResetPassword(() => setIsResetPasswordModalOpen(false));
  const { setTemporaryPassword, isSettingPassword } = useTemporaryPassword(() =>
    setIsTempPasswordSuccess(true),
  );

  const {
    enrollment,
    isLoading,
    isError,
    statusForm,
    updateStatusForm,
    resetStatusForm,
    changeStatus,
  } = useEnrollmentManagement(enrollmentId);

  const { details: detailsData, isLoading: isLoadingDetails } = useEnrollmentDetails(enrollmentId);

  const {
    documents,
    isRefetching: isRefetchingDocuments,
    refetch: refetchDocuments,
  } = useEnrollmentDocuments(enrollmentId);
  const { financialSummary } = useEnrollmentFinancial(enrollmentId);
  const { history, poloChanges } = useEnrollmentHistory(enrollmentId);
  const { courseAccess: tabCourseAccess, startDate: tabStartDate } =
    useEnrollmentCourseAccess(enrollmentId);
  const { scholarships } = useEnrollmentScholarship(enrollmentId);
  const { tabs } = useEnrollmentManagementTabs(enrollmentId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-xxs">
        <Spinner />
      </div>
    );
  }

  if (isError || !enrollment) {
    return (
      <div className="flex items-center justify-center p-xxs">
        <span className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('enrollmentDetail.notFound')}
        </span>
      </div>
    );
  }

  const initials = getInitials(enrollment.personName);
  const approvedCount = documents.filter(
    d => d.status === 'approved' || d.status === 'approved_temporary',
  ).length;
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const rejectedCount = documents.filter(d => d.status === 'rejected').length;
  const notSentCount = documents.filter(d => d.status === 'not_sent').length;

  const lastHistory = enrollment.history.at(-1);
  const isCancelled =
    enrollment.status === 'canceled' || enrollment.status === 'canceled_in_registration';

  const handleCloseDrawer = () => {
    setIsStatusDrawerOpen(false);
    resetStatusForm();
  };

  return (
    <div className="flex flex-col gap-xs">
      {/* ── Título ── */}
      <div className="flex flex-col gap-xs sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="ts-heading-md text-ctx-content-title">
            {t('enrollmentManagement.title')}
          </h1>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('enrollmentManagement.documentSubtitle', {
              total: documents.length,
              approved: approvedCount,
              pending: pendingCount + notSentCount,
              rejected: rejectedCount,
            })}
          </p>
        </div>
        {!isCancelled && (
          <Button
            hierarchy="danger"
            size="sm"
            leadingIcon={XCircleIcon}
            onClick={() => setIsCancelModalOpen(true)}
          >
            {t('enrollmentManagement.cancelEnrollment')}
          </Button>
        )}
      </div>

      {/* ── Card do aluno ── */}
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        {/* Topo: avatar + info + ações */}
        <div className="flex flex-col gap-xs p-xxs md:flex-row md:items-start">
          {/* Avatar + Nome + contato + tags */}
          <div className="flex min-w-none items-start gap-xs md:flex-1">
            {/* Avatar */}
            <div className="flex h-[2.5rem] w-[2.5rem] shrink-0 items-center justify-center rounded-circular bg-ctx-content-title">
              <span className="ts-subtitle-sm !font-bold text-ctx-content-titleAlternative">
                {initials}
              </span>
            </div>

            {/* Nome + contato + tags */}
            <div className="flex min-w-none flex-1 flex-col gap-quark">
              <h2 className="ts-subtitle-xs font-bold text-ctx-content-title">
                {enrollment.personName}
              </h2>
              <p className="ts-caption-xxs text-ctx-content-placeholder break-words">
                {enrollment.personEmail}
                {enrollment.personCpf && ` · CPF ${enrollment.personCpf}`}
                {enrollment.personPhoneMobile && ` · ${enrollment.personPhoneMobile}`}
              </p>
              <div className="flex flex-wrap gap-quark">
                {enrollment.scholarshipStatus !== 'none' && enrollment.scholarships[0] && (
                  <Tag
                    label={`Bolsista ${enrollment.scholarshipStatus === 'partial' ? 'Parcial' : 'Integral'} ${enrollment.scholarships[0].schoolDiscount}%`}
                    size="sm"
                    status="helper"
                    custom={{ text: 'text-nowrap' }}
                  />
                )}
                {enrollment.isIndicatedByStudent && (
                  <Tag
                    label={t('enrollmentDetail.indicatedByStudent')}
                    size="sm"
                    status="default"
                    custom={{ text: 'text-nowrap' }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-wrap items-center gap-quark md:shrink-0">
            <Button hierarchy="secondary" size="sm">
              {t('enrollmentManagement.viewProfile')}
            </Button>
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={KeyIcon}
              onClick={() => setIsResetPasswordModalOpen(true)}
            >
              {t('enrollmentActions.resetPassword')}
            </Button>
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={LockClosedIcon}
              onClick={() => setIsTempPasswordModalOpen(true)}
            >
              {t('enrollmentManagement.tempPassword')}
            </Button>
            <Button hierarchy="secondary" size="sm" onClick={() => setIsStatusDrawerOpen(true)}>
              {t('enrollmentManagement.alterStatus')}
            </Button>
            <Dropdown
              modal={false}
              trigger={
                <IconButton
                  icon={EllipsisHorizontalIcon}
                  hierarchy="secondary"
                  size="sm"
                  ariaLabel={t('actions.moreOptions')}
                />
              }
              align="end"
            >
              <DropdownItem
                onSelect={() =>
                  navigate({
                    to: '/persons/$id',
                    params: { id: String(enrollment.personId) },
                  })
                }
              >
                <DropdownItemContent leadingIcon={PencilSquareIcon}>
                  {t('enrollmentActions.editPerson')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => setIsChangePoloModalOpen(true)}>
                <DropdownItemContent leadingIcon={MapPinIcon}>
                  {t('enrollmentActions.changePolo')}
                </DropdownItemContent>
              </DropdownItem>
              <DropdownItem
                onSelect={() =>
                  alert({
                    alertType: 'warning',
                    title: t('enrollmentActions.transferNotIntegrated'),
                  })
                }
              >
                <DropdownItemContent leadingIcon={ArrowPathRoundedSquareIcon}>
                  {t('enrollmentActions.transfer')}
                </DropdownItemContent>
              </DropdownItem>
              {!isCancelled && (
                <DropdownItem status="destructive" onSelect={() => setIsCancelModalOpen(true)}>
                  <DropdownItemContent leadingIcon={XCircleIcon}>
                    {t('enrollmentActions.cancel')}
                  </DropdownItemContent>
                </DropdownItem>
              )}
            </Dropdown>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 border-t-hairline border-ctx-layout-border md:grid-cols-5">
          <div className="flex flex-col gap-quark border-r-hairline border-b-hairline border-ctx-layout-border p-xxs md:border-b-0">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentDetail.currentStatus')}
            </span>
            <EnrollmentStatusBadge status={enrollment.status} />
            {lastHistory && (
              <span className="ts-caption-xxs text-ctx-content-placeholder">
                {t('enrollmentDetail.lastStatusChange')}{' '}
                {new Date(lastHistory.creationDate ?? '').toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-quark border-b-hairline border-ctx-layout-border p-xxs md:border-r-hairline md:border-b-0">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('table.columns.course')} · {t('enrollmentDetail.turma')}
            </span>
            <span className="ts-paragraph-xs font-medium text-ctx-content-title line-clamp-1">
              {enrollment.courseName}
            </span>
            <span className="ts-caption-xxs text-ctx-content-placeholder">
              {enrollment.className} · Turma {enrollment.enrollmentDate.slice(0, 4)}/1
            </span>
          </div>

          <div className="flex flex-col gap-quark border-r-hairline border-b-hairline border-ctx-layout-border p-xxs md:border-b-0">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentDetail.polo')}
            </span>
            <span className="ts-paragraph-xs font-medium text-ctx-content-title">
              {enrollment.poleName ?? '—'}
            </span>
            {!enrollment.poleId && (
              <span className="ts-caption-xxs text-ctx-content-placeholder">
                {t('enrollmentDetail.noPolo')}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-quark border-b-hairline border-ctx-layout-border p-xxs md:border-r-hairline md:border-b-0">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentDetail.enrolledSince')}
            </span>
            <span className="ts-paragraph-xs font-medium text-ctx-content-title">
              {new Date(enrollment.enrollmentDate).toLocaleDateString('pt-BR')}
            </span>
            <span className="ts-caption-xxs text-ctx-content-placeholder">
              {relativeMonths(enrollment.enrollmentDate)}
            </span>
          </div>

          <div className="col-span-2 flex flex-col gap-quark p-xxs md:col-span-1">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentTabs.documents')}
            </span>
            <p className="ts-paragraph-xs font-medium text-ctx-content-title">
              {approvedCount} {t('enrollmentDetail.docApproved')}
              {pendingCount > 0 && (
                <span className="text-warning-500">
                  {' · '}
                  {pendingCount} {t('enrollmentDetail.docPending')}
                </span>
              )}
            </p>
            {rejectedCount > 0 && (
              <span className="ts-caption-xxs text-ctx-content-placeholder">
                {rejectedCount} {t('enrollmentDetail.docRejected')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}

      <CancelEnrollmentModal
        enrollment={enrollment}
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={cancel}
      />

      {/* ── Polo Modal ── */}

      <ChangePoloModal
        enrollment={enrollment}
        open={isChangePoloModalOpen}
        onClose={() => setIsChangePoloModalOpen(false)}
        onConfirm={changePolo}
        isLoading={isChanging}
      />

      {/* ── Reset Password Modal ── */}
      <ResetPasswordModal
        personName={enrollment.personName}
        open={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        onConfirm={() => resetPassword(enrollment.personId)}
        isLoading={isResetting}
      />

      {/* ── Temporary Password Modal ── */}
      <TemporaryPasswordModal
        open={isTempPasswordModalOpen}
        isSuccess={isTempPasswordSuccess}
        onClose={() => {
          setIsTempPasswordModalOpen(false);
          setIsTempPasswordSuccess(false);
        }}
        onConfirm={password => setTemporaryPassword(enrollment.personId, password)}
        isLoading={isSettingPassword}
      />

      {/* ── Status Drawer ── */}
      <EnrollmentStatusDrawer
        isOpen={isStatusDrawerOpen}
        onClose={handleCloseDrawer}
        enrollment={enrollment}
        statusForm={statusForm}
        onUpdateStatusForm={updateStatusForm}
        onConfirm={() => {
          changeStatus();
          handleCloseDrawer();
        }}
      />

      {/* ── Course Access Drawer ── */}
      <EnrollmentCourseAccessDrawer
        isOpen={isCourseAccessDrawerOpen}
        onClose={() => setIsCourseAccessDrawerOpen(false)}
        enrollmentId={enrollmentId}
        courseAccess={tabCourseAccess}
        startDate={tabStartDate}
      />

      {/* ── Selective Process Drawer ── */}
      {enrollment.selectiveProcess != null && (
        <EnrollmentSelectiveProcessDrawer
          isOpen={isSelectiveProcessDrawerOpen}
          onClose={() => setIsSelectiveProcessDrawerOpen(false)}
          enrollmentId={enrollmentId}
          selectiveProcess={enrollment.selectiveProcess}
        />
      )}

      {/* ── Tabs ── */}
      <div className="">
        <TabMenu defaultValue="details">
          <TabMenuList className="w-full overflow-x-auto">
            {tabs.details && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="details">
                {t('enrollmentTabs.details')}
              </TabMenuTrigger>
            )}
            {tabs.documents && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="documents">
                <span className="flex items-center gap-quark">
                  {t('enrollmentTabs.documents')}
                  {pendingCount > 0 && (
                    <span className="inline-flex h-[1rem] min-w-[1rem] items-center justify-center rounded-pill bg-base-warning-500 px-quark ts-caption-xxs !font-medium text-base-neutral-white">
                      {pendingCount}
                    </span>
                  )}
                </span>
              </TabMenuTrigger>
            )}
            {tabs.financial && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="financial">
                {t('enrollmentTabs.financeiro')}
              </TabMenuTrigger>
            )}
            {tabs.scholarship && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="scholarship">
                {t('enrollmentTabs.scholarship')}
              </TabMenuTrigger>
            )}
            {tabs.academic && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="academic">
                {t('enrollmentTabs.academic')}
              </TabMenuTrigger>
            )}
            {tabs.history && (
              <TabMenuTrigger className="shrink-0 whitespace-nowrap md:flex-1" value="history">
                {t('enrollmentTabs.history')}
              </TabMenuTrigger>
            )}
          </TabMenuList>

          {/* ════ DETALHES ════ */}
          {tabs.details && (
            <TabMenuContent value="details">
              {detailsData ? (
                <EnrollmentDetailsTab
                  details={detailsData}
                  onEditPerson={() =>
                    navigate({
                      to: '/persons/$id',
                      params: { id: String(enrollment.personId) },
                    })
                  }
                />
              ) : isLoadingDetails ? (
                <div className="flex items-center justify-center p-xxs">
                  <Spinner />
                </div>
              ) : null}
            </TabMenuContent>
          )}

          {/* ════ DOCUMENTAÇÃO ════ */}
          {tabs.documents && (
            <TabMenuContent value="documents">
              <EnrollmentDocumentationTab
                documents={documents}
                personId={enrollment.personId}
                enrollmentId={enrollmentId}
                isRefetching={isRefetchingDocuments}
                onRefetch={() => void refetchDocuments()}
              />
            </TabMenuContent>
          )}

          {/* ════ FINANCEIRO ════ */}
          {tabs.financial && (
            <TabMenuContent value="financial">
              <EnrollmentFinancialTab
                financialSummary={financialSummary}
                onOpenPayments={onOpenPayments ?? (() => {})}
              />
            </TabMenuContent>
          )}

          {/* ════ BOLSA ════ */}
          {tabs.scholarship && (
            <TabMenuContent value="scholarship">
              <EnrollmentScholarshipTab
                scholarships={scholarships}
                scholarshipFull={enrollment.scholarshipFull ?? null}
                onAlterStatus={() => {}}
                onExtendDeadline={() => {}}
              />
            </TabMenuContent>
          )}

          {/* ════ ACADÊMICO ════ */}
          {tabs.academic && (
            <TabMenuContent value="academic">
              <EnrollmentAcademicTab
                startDate={tabStartDate}
                courseAccess={tabCourseAccess}
                {...(enrollment.isIndicatedByStudent !== undefined
                  ? { isIndicatedByStudent: enrollment.isIndicatedByStudent }
                  : {})}
                {...(enrollment.aiaDetails !== undefined
                  ? { aiaDetails: enrollment.aiaDetails }
                  : {})}
                selectiveProcess={enrollment.selectiveProcess}
                poleName={enrollment.poleName}
                poleId={enrollment.poleId}
                onEditCourseAccess={() => setIsCourseAccessDrawerOpen(true)}
                {...(enrollment.selectiveProcess != null
                  ? {
                      onEditSelectiveProcess: () => setIsSelectiveProcessDrawerOpen(true),
                    }
                  : {})}
              />
            </TabMenuContent>
          )}

          {/* ════ HISTÓRICO ════ */}
          {tabs.history && (
            <TabMenuContent value="history">
              <EnrollmentHistoryTab
                history={history}
                poloChanges={poloChanges}
                poleName={enrollment.poleName}
                poleId={enrollment.poleId}
                enrollmentDate={enrollment.enrollmentDate}
              />
            </TabMenuContent>
          )}
        </TabMenu>
      </div>
    </div>
  );
}
